import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getProfitMargins,
  previewProfitMargins,
  type ProfitMarginConfiguration,
  type ProfitMarginDirection,
  type ProfitMarginInput,
  type ProfitMarginPreview,
  updateProfitMargins,
} from "@/services/Market.service";
import { Storage } from "@/services/storage-services";
import { AxiosError } from "axios";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ProfitMarginCard } from "./ProfitMarginCard";
import { formatMargin } from "./market-margin.utils";

type Drafts = Record<ProfitMarginDirection, ProfitMarginInput>;

const DECIMAL_PATTERN = /^(?:0|[1-9]\d{0,11})(?:\.\d{1,8})?$/;

function draftsFromConfiguration(config: ProfitMarginConfiguration): Drafts {
  return {
    onRamp: {
      marginType: config.onRamp.marginType,
      marginValue: config.onRamp.marginValue,
    },
    offRamp: {
      marginType: config.offRamp.marginType,
      marginValue: config.offRamp.marginValue,
    },
  };
}

function validateMargin(
  input: ProfitMarginInput,
  providerRate: string | null,
): string | null {
  if (!DECIMAL_PATTERN.test(input.marginValue)) {
    return "Enter zero or a positive number with up to 8 decimal places.";
  }
  const value = Number(input.marginValue);
  if (!Number.isFinite(value) || value < 0) {
    return "Margin cannot be negative.";
  }
  if (input.marginType === "PERCENTAGE" && value >= 100) {
    return "Percentage margin must be less than 100%.";
  }
  if (
    input.marginType === "FLAT" &&
    providerRate !== null &&
    value >= Number(providerRate)
  ) {
    return "Flat margin must be less than the provider rate.";
  }
  return null;
}

function sameMargin(
  draft: ProfitMarginInput,
  current: ProfitMarginInput,
): boolean {
  return (
    draft.marginType === current.marginType &&
    Number(draft.marginValue) === Number(current.marginValue)
  );
}

function displayRate(value: string | null): string {
  if (value === null) return "Unavailable";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

type LivePreviewState = {
  result: ProfitMarginPreview | null;
  error: string | null;
  isPreviewing: boolean;
};

function useLiveMarginPreview(
  direction: ProfitMarginDirection,
  config: ProfitMarginConfiguration | null,
  draft: ProfitMarginInput | null,
  enabled: boolean,
): LivePreviewState {
  const sequence = useRef(0);
  const [state, setState] = useState<LivePreviewState>({
    result: null,
    error: null,
    isPreviewing: false,
  });

  useEffect(() => {
    sequence.current += 1;
    const requestSequence = sequence.current;
    if (!config || !draft || !enabled) {
      setState({ result: null, error: null, isPreviewing: false });
      return;
    }

    const localError = validateMargin(draft, config[direction].providerRate);
    if (localError || sameMargin(draft, config[direction])) {
      setState({ result: null, error: null, isPreviewing: false });
      return;
    }

    const controller = new AbortController();
    setState({ result: null, error: null, isPreviewing: true });
    const timer = window.setTimeout(() => {
      void previewProfitMargins({ [direction]: draft }, controller.signal)
        .then((result) => {
          if (requestSequence !== sequence.current) return;
          const proposed = result.proposed[direction];
          if (
            !result.valid ||
            proposed.providerRate === null ||
            proposed.customerRate === null
          ) {
            setState({
              result: null,
              error:
                result.errors[direction] ??
                `${proposed.provider === "quidax" ? "Quidax" : "Treasura"} rate is unavailable. This margin cannot be reviewed or saved.`,
              isPreviewing: false,
            });
            return;
          }
          setState({ result, error: null, isPreviewing: false });
        })
        .catch((error: unknown) => {
          if (
            requestSequence !== sequence.current ||
            (error instanceof AxiosError && error.code === "ERR_CANCELED")
          ) {
            return;
          }
          const message =
            error instanceof AxiosError
              ? error.response?.data?.message
              : "The proposed customer rate could not be calculated.";
          setState({
            result: null,
            error:
              message || "The proposed customer rate could not be calculated.",
            isPreviewing: false,
          });
        });
    }, 400);

    return () => {
      if (sequence.current === requestSequence) {
        sequence.current += 1;
      }
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [config, direction, draft, enabled]);

  return state;
}

const Markets = () => {
  const [config, setConfig] = useState<ProfitMarginConfiguration | null>(null);
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingDirection, setPendingDirection] =
    useState<ProfitMarginDirection | null>(null);
  const [preview, setPreview] = useState<ProfitMarginPreview | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const roleId = Storage.getItem("selectedMerchant")?.roleId;
  const canEdit = roleId === "merchant-owner" || roleId === "merchant-admin";

  const onRampPreview = useLiveMarginPreview(
    "onRamp",
    config,
    drafts?.onRamp ?? null,
    canEdit,
  );
  const offRampPreview = useLiveMarginPreview(
    "offRamp",
    config,
    drafts?.offRamp ?? null,
    canEdit,
  );

  const loadConfiguration = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const nextConfig = await getProfitMargins();
      setConfig(nextConfig);
      setDrafts(draftsFromConfiguration(nextConfig));
    } catch {
      setLoadError("Pricing margin settings could not be loaded. Try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  const reviewChange = (direction: ProfitMarginDirection) => {
    const livePreview = direction === "onRamp" ? onRampPreview : offRampPreview;
    if (!livePreview.result || livePreview.isPreviewing || livePreview.error) {
      return;
    }
    setPreview(livePreview.result);
    setPendingDirection(direction);
  };

  const applyChange = async () => {
    if (!config || !drafts || !pendingDirection) return;
    setIsSaving(true);
    try {
      const updated = await updateProfitMargins({
        version: config.version,
        [pendingDirection]: drafts[pendingDirection],
      });
      setConfig(updated);
      setDrafts(draftsFromConfiguration(updated));
      setPendingDirection(null);
      setPreview(null);
      toast.success("Pricing margin updated successfully.");
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        setPendingDirection(null);
        setPreview(null);
        toast.error(
          "Another administrator changed these settings. The latest values were loaded.",
        );
        await loadConfiguration();
      } else {
        const message =
          error instanceof AxiosError
            ? error.response?.data?.message
            : "Pricing margin could not be updated.";
        toast.error(message || "Pricing margin could not be updated.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-8 p-6 py-12">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 xl:grid-cols-2">
          <Skeleton className="h-[430px] rounded-xl" />
          <Skeleton className="h-[430px] rounded-xl" />
        </div>
      </div>
    );
  }

  if (loadError || !config || !drafts) {
    return (
      <div className="grid min-h-[420px] place-items-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-600" />
          <h1 className="mt-4 text-xl font-semibold text-primary-3">
            Unable to load settings
          </h1>
          <p className="mt-2 text-sm text-gray-70">{loadError}</p>
          <Button
            className="mt-5 rounded-lg"
            onClick={() => void loadConfiguration()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const errors = {
    onRamp: validateMargin(drafts.onRamp, config.onRamp.providerRate),
    offRamp: validateMargin(drafts.offRamp, config.offRamp.providerRate),
  };
  const changes = {
    onRamp: !sameMargin(drafts.onRamp, config.onRamp),
    offRamp: !sameMargin(drafts.offRamp, config.offRamp),
  };
  const currentDirection = pendingDirection
    ? preview?.current[pendingDirection]
    : null;
  const proposedDirection = pendingDirection
    ? preview?.proposed[pendingDirection]
    : null;

  return (
    <div className="grid gap-8 p-6 py-12">
      <header>
        <p className="text-sm font-medium text-gray-70">Markets → Settings</p>
        <h1 className="mt-1 text-2xl font-semibold text-primary-3">
          Pricing Margin Settings
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-70">
          Set the platform-wide pricing margin included in customer exchange
          rates. Customer rates are calculated and confirmed by the backend.
        </p>
      </header>

      {!canEdit ? (
        <div className="rounded-lg border border-[#EAE6EA] bg-[#F8F7F8] p-4 text-sm text-gray-70">
          You have read-only access. Only owners and administrators can change
          pricing margins.
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <ProfitMarginCard
          direction="onRamp"
          title="On-Ramp"
          config={config.onRamp}
          draft={drafts.onRamp}
          error={errors.onRamp}
          previewError={onRampPreview.error}
          proposed={onRampPreview.result?.proposed.onRamp ?? null}
          canEdit={canEdit}
          isPreviewing={onRampPreview.isPreviewing}
          isPreviewValid={Boolean(onRampPreview.result?.valid)}
          hasChanges={changes.onRamp}
          onChange={(value) =>
            setDrafts((current) =>
              current ? { ...current, onRamp: value } : current,
            )
          }
          onReview={() => reviewChange("onRamp")}
        />
        <ProfitMarginCard
          direction="offRamp"
          title="Off-Ramp"
          config={config.offRamp}
          draft={drafts.offRamp}
          error={errors.offRamp}
          previewError={offRampPreview.error}
          proposed={offRampPreview.result?.proposed.offRamp ?? null}
          canEdit={canEdit}
          isPreviewing={offRampPreview.isPreviewing}
          isPreviewValid={Boolean(offRampPreview.result?.valid)}
          hasChanges={changes.offRamp}
          onChange={(value) =>
            setDrafts((current) =>
              current ? { ...current, offRamp: value } : current,
            )
          }
          onReview={() => reviewChange("offRamp")}
        />
      </div>

      <p className="text-xs text-gray-70">
        Last updated {new Date(config.updatedAt).toLocaleString("en-NG")} ·
        Version {config.version}
      </p>

      <CustomModal
        open={Boolean(pendingDirection && preview)}
        onOpenChange={(open: boolean) => {
          if (!open && !isSaving) {
            setPendingDirection(null);
            setPreview(null);
          }
        }}
        size="md"
      >
        <div className="p-6">
          <DialogHeader>
            <DialogTitle>Confirm pricing margin change</DialogTitle>
          </DialogHeader>
          {pendingDirection && currentDirection && proposedDirection ? (
            <div className="mt-5 space-y-5">
              <p className="text-sm text-gray-70">
                {pendingDirection === "onRamp" ? "On-Ramp" : "Off-Ramp"} margin
              </p>
              <div className="rounded-lg bg-[#F8F7F8] p-4 text-sm">
                <p className="font-medium text-primary-3">
                  {formatMargin(currentDirection)} →{" "}
                  {formatMargin(proposedDirection)}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-70">Current rate</p>
                    <p className="mt-1 font-semibold">
                      {displayRate(currentDirection.providerRate)} →{" "}
                      {displayRate(currentDirection.customerRate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-70">Proposed rate</p>
                    <p className="mt-1 font-semibold">
                      {displayRate(proposedDirection.providerRate)} →{" "}
                      {displayRate(proposedDirection.customerRate)}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-70">
                This new rate adjustment will apply to future transactions after
                you confirm.
              </p>
            </div>
          ) : null}
          <DialogFooter className="mt-6 gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={() => {
                setPendingDirection(null);
                setPreview(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isSaving}
              onClick={() => void applyChange()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying
                </>
              ) : (
                "Apply change"
              )}
            </Button>
          </DialogFooter>
        </div>
      </CustomModal>
    </div>
  );
};

export default Markets;
